/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";
import { Upload, File, FileText, X } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { BaseFieldProps } from "./form-field-utils";
import { FormFieldLabel } from "./form-field-label";

export function FieldFile({
    name,
    label,
    description,
    className,
    labelType = "text",
    tooltipContent,
    accept,
    multiple = false,
    ...rest
}: BaseFieldProps & Omit<React.ComponentProps<typeof Input>, "type"> & { multiple?: boolean }) {
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const getFileIcon = (fileName?: string) => {
        if (!fileName) return <Upload className="h-8 w-8" />;
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (['pdf'].includes(ext || '')) return <FileText className="h-8 w-8" />;
        return <File className="h-8 w-8" />;
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const isImageFile = (file?: File) => {
        if (!file) return false;
        return file.type.startsWith('image/');
    };

    return (
        <FormField
            name={name}
            render={({ field: { value, onChange, ...fieldProps } }) => {
                // Handle both single file and multiple files
                const files: File[] = multiple
                    ? (Array.isArray(value) ? value : value ? [value] : [])
                    : (value ? [value as File] : []);
                const [imagePreviews, setImagePreviews] = useState<Record<string, string>>({});
                const [selectedPreview, setSelectedPreview] = useState<string | null>(null);

                // Create image previews when files change
                useEffect(() => {
                    const newPreviews: Record<string, string> = {};
                    const readers: FileReader[] = [];

                    files.forEach((file, index) => {
                        if (isImageFile(file)) {
                            const reader = new FileReader();
                            readers.push(reader);
                            reader.onload = (e) => {
                                newPreviews[`${file.name}-${index}`] = e.target?.result as string;
                                if (Object.keys(newPreviews).length === files.filter(f => isImageFile(f)).length) {
                                    setImagePreviews({ ...newPreviews });
                                }
                            };
                            reader.readAsDataURL(file);
                        }
                    });

                    if (files.filter(f => isImageFile(f)).length === 0) {
                        setImagePreviews({});
                    }

                    return () => {
                        readers.forEach(reader => reader.abort());
                    };
                }, [files.length, files.map(f => f.name).join(',')]);

                const handleDrop = (e: React.DragEvent) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const droppedFiles = Array.from(e.dataTransfer.files);
                    if (droppedFiles.length > 0) {
                        if (multiple) {
                            onChange([...files, ...droppedFiles]);
                        } else {
                            onChange(droppedFiles[0]);
                        }
                    }
                };

                const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const selectedFiles = Array.from(e.target.files || []);
                    if (multiple) {
                        onChange([...files, ...selectedFiles]);
                    } else {
                        onChange(selectedFiles[0]);
                    }
                    // Reset input value to allow selecting same file again
                    if (inputRef.current) {
                        inputRef.current.value = '';
                    }
                };

                const handleRemove = (index?: number) => {
                    if (multiple && typeof index === 'number') {
                        const newFiles = files.filter((_, i) => i !== index);
                        onChange(newFiles.length > 0 ? newFiles : undefined);
                    } else {
                        onChange(undefined);
                    }
                    setImagePreviews({});
                    if (inputRef.current) {
                        inputRef.current.value = '';
                    }
                };

                return (
                    <FormItem className={className}>
                        <FormFieldLabel label={label} labelType={labelType} tooltipContent={tooltipContent} />
                        <FormControl>
                            <div className="space-y-4">
                                <div
                                    onClick={() => inputRef.current?.click()}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={`relative cursor-pointer rounded-lg border-2 border-dashed transition-all duration-200 ${isDragging
                                        ? 'border-primary bg-primary/5 scale-[1.02]'
                                        : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                                        } ${files.length > 0 ? 'bg-muted/50' : ''}`}
                                >
                                    <Input
                                        {...fieldProps}
                                        ref={(e) => {
                                            fieldProps.ref(e);
                                            (inputRef as any).current = e;
                                        }}
                                        type="file"
                                        accept={accept}
                                        multiple={multiple}
                                        onChange={handleFileChange}
                                        className="hidden"
                                        {...rest}
                                    />

                                    {files.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                                            <div className="rounded-full bg-primary/10 p-4 text-primary">
                                                <Upload className="h-8 w-8" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-sm font-medium">
                                                    {multiple
                                                        ? 'Dosyaları seçmek için tıklayın veya sürükleyip bırakın'
                                                        : 'Dosya seçmek için tıklayın veya sürükleyip bırakın'
                                                    }
                                                </p>
                                                {accept && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Desteklenen formatlar: {accept}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-4 space-y-2">
                                            {files.map((file, index) => {
                                                const previewKey = `${file.name}-${index}`;
                                                const preview = imagePreviews[previewKey];
                                                return (
                                                    <div key={previewKey} className="flex items-center gap-3 p-2 bg-background rounded-lg">
                                                        {/* Thumbnail veya Icon */}
                                                        {preview ? (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedPreview(preview);
                                                                }}
                                                                className="relative w-12 h-12 rounded-lg overflow-hidden border hover:ring-2 hover:ring-primary transition-all shrink-0"
                                                            >
                                                                <img
                                                                    src={preview}
                                                                    alt={file.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            </button>
                                                        ) : (
                                                            <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
                                                                {getFileIcon(file.name)}
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium break-all">{file.name}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {formatFileSize(file.size)}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemove(multiple ? index : undefined);
                                                            }}
                                                            className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive shrink-0"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                );
                                            })}
                                            {multiple && (
                                                <p className="text-xs text-center text-muted-foreground pt-2">
                                                    Daha fazla dosya eklemek için tıklayın
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Büyük Önizleme Modal */}
                                {selectedPreview && (
                                    <Dialog open={true} onOpenChange={(open) => !open && setSelectedPreview(null)}>
                                        <DialogContent className="max-w-4xl w-fit p-0 border-0 bg-transparent shadow-none">
                                            <DialogTitle className="sr-only">Önizleme</DialogTitle>
                                            <div className="relative inline-block">
                                                <img
                                                    src={selectedPreview}
                                                    alt="Önizleme"
                                                    className="max-w-full max-h-[85vh] object-contain rounded-lg"
                                                />
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}
